/**
 * Regional Pricing Configuration
 * Same numeric value, different currencies per region
 */

export const REGIONAL_CONFIG = {
  'pt-BR': {
    currency: 'BRL',
    symbol: 'R$',
    symbolPosition: 'before', // R$ 97
    locale: 'pt-BR',
    paypalCurrency: 'BRL',
    decimalSeparator: ',',
    thousandSeparator: '.'
  },
  'en-US': {
    currency: 'USD',
    symbol: '$',
    symbolPosition: 'before', // $97
    locale: 'en-US',
    paypalCurrency: 'USD',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  'es-ES': {
    currency: 'EUR',
    symbol: '€',
    symbolPosition: 'after', // 97€
    locale: 'es-ES',
    paypalCurrency: 'EUR',
    decimalSeparator: ',',
    thousandSeparator: '.'
  }
};

const PRICING_BASE = {
  basic: {
    monthly: 97,
    annual: 970, // ~17% desconto (equivale a 10 meses)
    clientLimit: 5
  },
  professional: {
    monthly: 197,
    annual: 1970,
    clientLimit: 15
  },
  enterprise: {
    monthly: 297,
    annual: 2970,
    clientLimit: 30
  },
  ondemand: {
    perClient: 9,
    minClients: 31,
    maxClients: 1000
  }
};

/** Formats a price with currency symbol */
export function formatPrice(value, locale = 'pt-BR') {
  const config = REGIONAL_CONFIG[locale] || REGIONAL_CONFIG['en-US'];
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);
}

/** Formats price compactly (no decimals when integer) */
export function formatPriceCompact(value, locale = 'pt-BR') {
  const config = REGIONAL_CONFIG[locale] || REGIONAL_CONFIG['en-US'];
  const isInteger = value % 1 === 0;

  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: isInteger ? 0 : 2
  }).format(value);
}

/** Gets the currency symbol */
export function getCurrencySymbol(locale = 'pt-BR') {
  const config = REGIONAL_CONFIG[locale] || REGIONAL_CONFIG['en-US'];
  return config.symbol;
}

/** Gets PayPal currency code */
export function getPayPalCurrency(locale = 'pt-BR') {
  const config = REGIONAL_CONFIG[locale] || REGIONAL_CONFIG['en-US'];
  return config.paypalCurrency;
}

/** Gets pricing configuration for a specific locale */
export function getPricingForLocale(locale = 'pt-BR') {
  const config = REGIONAL_CONFIG[locale] || REGIONAL_CONFIG['en-US'];

  return {
    currency: config.currency,
    symbol: config.symbol,

    basic: {
      name: 'Basic',
      clientLimit: PRICING_BASE.basic.clientLimit,
      monthlyPrice: PRICING_BASE.basic.monthly,
      annualPrice: PRICING_BASE.basic.annual,
      formattedMonthly: formatPriceCompact(PRICING_BASE.basic.monthly, locale),
      formattedAnnual: formatPriceCompact(PRICING_BASE.basic.annual, locale),
      formattedMonthlyFromAnnual: formatPriceCompact(PRICING_BASE.basic.annual / 12, locale)
    },

    professional: {
      name: 'Professional',
      clientLimit: PRICING_BASE.professional.clientLimit,
      monthlyPrice: PRICING_BASE.professional.monthly,
      annualPrice: PRICING_BASE.professional.annual,
      formattedMonthly: formatPriceCompact(PRICING_BASE.professional.monthly, locale),
      formattedAnnual: formatPriceCompact(PRICING_BASE.professional.annual, locale),
      formattedMonthlyFromAnnual: formatPriceCompact(PRICING_BASE.professional.annual / 12, locale),
      mostPopular: true
    },

    enterprise: {
      name: 'Enterprise',
      clientLimit: PRICING_BASE.enterprise.clientLimit,
      monthlyPrice: PRICING_BASE.enterprise.monthly,
      annualPrice: PRICING_BASE.enterprise.annual,
      formattedMonthly: formatPriceCompact(PRICING_BASE.enterprise.monthly, locale),
      formattedAnnual: formatPriceCompact(PRICING_BASE.enterprise.annual, locale),
      formattedMonthlyFromAnnual: formatPriceCompact(PRICING_BASE.enterprise.annual / 12, locale)
    },

    ondemand: {
      name: 'On Demand',
      minClients: PRICING_BASE.ondemand.minClients,
      maxClients: PRICING_BASE.ondemand.maxClients,
      perClientPrice: PRICING_BASE.ondemand.perClient,
      formattedPerClient: formatPriceCompact(PRICING_BASE.ondemand.perClient, locale)
    }
  };
}

